import { Link, useLocation } from 'react-router-dom';
import { IconButton } from '@mui/material';
import { MdChevronRight, MdMenu } from 'react-icons/md';
import { findNavMatch } from './nav.config';

interface Crumb {
  label: string;
  to?: string;
}

interface Props {
  /** Called when the mobile-only hamburger is tapped. */
  onMobileMenuClick: () => void;
}

function resolveCrumbs(pathname: string): Crumb[] {
  const match = findNavMatch(pathname);
  if (!match) return [{ label: 'Home', to: '/home' }];

  // Dashboard itself is the root — show only "Home" as current.
  if (match.item.to === '/home') {
    return [{ label: 'Home' }];
  }

  return [
    { label: 'Home', to: '/home' },
    { label: match.section.label },
    { label: match.item.label },
  ];
}

export function AppShellTopBar({ onMobileMenuClick }: Props) {
  const location = useLocation();
  const crumbs = resolveCrumbs(location.pathname);
  // On mobile, only the last crumb (the current page) is shown — earlier
  // crumbs eat horizontal space and the hamburger already implies "back to
  // navigation". The full trail returns at md and above.
  const currentCrumb = crumbs[crumbs.length - 1];

  return (
    <header className="relative flex h-14 shrink-0 items-center justify-between gap-2 border-b border-border bg-surface px-3 md:px-6">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-px"
        style={{
          background:
            'linear-gradient(90deg, transparent, rgb(var(--color-primary) / 0.35), transparent)',
        }}
      />

      <div className="flex min-w-0 items-center gap-2">
        <IconButton
          onClick={onMobileMenuClick}
          aria-label="Open navigation"
          size="small"
          className="md:hidden"
        >
          <MdMenu />
        </IconButton>

        <nav
          aria-label="Breadcrumb"
          className="flex min-w-0 items-center gap-1.5 text-[13px]"
        >
          <span className="truncate font-medium text-foreground md:hidden">
            {currentCrumb.label}
          </span>

          <div className="hidden items-center gap-1.5 md:flex">
            {crumbs.map((c, i) => {
              const isLast = i === crumbs.length - 1;
              return (
                <span key={`${c.label}-${i}`} className="flex items-center gap-1.5">
                  {i > 0 && (
                    <MdChevronRight className="text-foreground-muted" size={14} />
                  )}
                  {c.to && !isLast ? (
                    <Link
                      to={c.to}
                      className="text-foreground-muted transition-colors hover:text-foreground"
                    >
                      {c.label}
                    </Link>
                  ) : (
                    <span
                      className={
                        isLast ? 'font-medium text-foreground' : 'text-foreground-muted'
                      }
                    >
                      {c.label}
                    </span>
                  )}
                </span>
              );
            })}
          </div>
        </nav>
      </div>

      <div
        id="app-shell-topbar-actions"
        className="flex items-center gap-2"
        aria-label="Page actions"
      />
    </header>
  );
}
